import type { UnknownAction } from "@reduxjs/toolkit";
import _ from "lodash";
import { orderBy } from "natural-orderby";
import {
  FETCHING_HUB_LAYOUTS,
  FETCHING_HUB_LAYOUTS_SUCCEEDED,
  FETCHING_HUB_LAYOUTS_FAILED,
  FETCHING_HUB_LAYOUT,
  FETCHING_HUB_LAYOUT_SUCCEEDED,
  UPDATING_HUB_LAYOUT,
  UPDATING_HUB_LAYOUT_SUCCEEDED,
  UPDATING_HUB_LAYOUT_FAILED,
  DELETING_HUB_SECTION,
  DELETING_HUB_SECTION_SUCCEEDED,
  DELETING_HUB_SECTION_FAILED,
  FETCHING_HUB_SECTION,
  FETCHING_HUB_SECTION_SUCCEEDED,
  FETCHING_HUB_SECTION_FAILED,
  DELETING_HUB_LAYOUT,
  UPDATING_HUB_SECTION,
  UPDATING_HUB_SECTION_SUCCEEDED,
  UPDATING_HUB_SECTION_FAILED,
  FETCHING_HUB_LAYOUT_FAILED,
  DELETING_HUB_LAYOUT_SUCCEEDED,
  DELETING_HUB_LAYOUT_FAILED,
  FETCHING_STREAMS_REQUEST,
  FETCHING_STREAMS_SUCCEEDED,
  FETCHING_STREAMS_FAILED,
  REARRANGING_HUB_LAYOUT_SUCCEEDED,
  HIDE_SIDE_MENU,
  INVALIDATE_HUB_LAYOUT_UPDATE,
  ENABLING_LAYOUT_ACCESS_FOR_USER_GROUPS,
  ENABLING_LAYOUT_ACCESS_FOR_USER_GROUPS_SUCCEEDED,
  ENABLING_LAYOUT_ACCESS_FOR_USER_GROUPS_FAILED,
  CREATE_HUB_LAYOUT,
  CREATING_HUB_LAYOUT_SUCCEEDED,
  CREATING_HUB_LAYOUT_FAILED,
  INVALIDATE_HUB_LAYOUT_CREATION
} from "../actions/actionTypes";
import { HubLayout, HubLayoutSection, UpdateHubSectionResponse } from "../../types/hub";
import { FetchStreamsErrorResponse, FetchStreamsResponse, Stream } from "src/types/streams";
import { error } from "console";

interface LayoutState {
  loading: boolean;
  invalidated: boolean;
  item: any;
  layouts: any[];
  error: any;
  rearranged: boolean;
  deleting: boolean;
  creating: boolean;
  enablingAccess: boolean;
  sections: any;
  streams: StreamsState;
  layoutsState: LayoutsState,
  [key: string]: any; // Add index signature,
  sectionDeletion:{
    loading : boolean;
    error : any;
    succeeded : boolean;
  },
  defaultLayout:{
    creating: boolean;
    error: any;
    succeeded: boolean;
    item: any
  }
}

interface LayoutsState{
  loading: boolean;
  error: any,
  succeeded: boolean;
}

const initialLayoutState: LayoutState = {
  loading: false,
  invalidated: false,
  item: null,
  layouts: [],
  error: null,
  rearranged: false,
  deleting: false,
  creating: false,
  enablingAccess: false,
  sections: [],
  streams: {
    loading: false,
    loaded: false,
    items: [],
    error: null,
  },
  hideSideMenu : false,
  layoutsState: {
    loading: false,
    error: null,
    succeeded: false,
  },
  sectionDeletion:{
    loading : false,
    error : null,
    succeeded : false
  },
  defaultLayout: {
    creating: false,
    error: null,
    succeeded: false,
    item: null
  }
};

interface SectionState {
  loading: boolean;
  invalidated: boolean;
  items: any[];
}

const initialSectionState: SectionState = {
  loading: true,
  invalidated: true,
  items: [],
};

interface HubLayoutAction {
  [key: string]: any;
}

interface StreamsState {
  loading: boolean;
  loaded: boolean;
  items?: Stream[];
  error?: any;
}

type Action = HubLayoutAction;

export function hubReducer(state = initialLayoutState, action: Action) {
  switch (action.type) {
    case FETCHING_HUB_LAYOUT:
      return {
        ...state,
        loading: true,
        invalidated: true,
      };

    case FETCHING_HUB_LAYOUT_SUCCEEDED: {
      const response = action.payload as HubLayout;

      return {
        ...state,
        loading: false,
        invalidated: false,
        item: response,
      };
    }
    case FETCHING_HUB_LAYOUT_FAILED:
      return {
        ...state,
        loading: false,
        invalidated: false,
      };

    case FETCHING_HUB_LAYOUTS:
      return {
        ...state,
        loading: true,
        invalidated: true,
        layouts: [],
        layoutsState: {
          loading: true,
          error: null,
          succeeded: false,
        }
      };

    case FETCHING_HUB_LAYOUTS_SUCCEEDED:
      return {
        ...state,
        loading: false,
        invalidated: false,
        layouts: action.payload,
        layoutsState: {
          loading: false,
          error: null,
          succeeded: true
        }
      };
      break;

    case FETCHING_HUB_LAYOUTS_FAILED:
      return {
        ...state,
        loading: false,
        invalidated: false,
        error: action.payload,
        layoutsState: {
          loading: false,
          error: action.payload,
          succeeded: false,
        }
      };

    // case INVALIDATE_HUB_LAYOUT:
    //   return {
    //     ...state,
    //     loading: false,
    //     invalidated: true,
    //   };

    case REARRANGING_HUB_LAYOUT_SUCCEEDED: {
      const { rowIndex, direction } = action.payload;
      const { rows } = state.item;

      if (
        (rowIndex === 0 && direction === "up") ||
        (rowIndex === rows.length - 1 && direction === "down")
      ) {
        return state;
      }

      const newIndex = direction === "up" ? rowIndex - 1 : rowIndex + 1;
      const updatedRows = [...rows];
      const [movedRow] = updatedRows.splice(rowIndex, 1);
      updatedRows.splice(newIndex, 0, movedRow);

      return {
        ...state,
        loading: false,
        invalidated: false,
        item: {
          ...state.item,
          rows: updatedRows,
        },
        rearranged: true,
      };
    }

    case UPDATING_HUB_LAYOUT:
      return {
        ...state,
        update:{
          loading : true,
          error : null,
          succeeded : false
        }
      };

    case UPDATING_HUB_LAYOUT_SUCCEEDED:
      return {
        ...state,
        invalidated: false,
        item: action.payload,
        rearranged: false,
        update:{
          loading : false,
          error : null,
          succeeded : true
        },
        layouts: state.layouts.map((layout) =>
          layout.id === action.payload.id ? action.payload : layout
        ),
      };

    case UPDATING_HUB_LAYOUT_FAILED:
      return {
        ...state,
        invalidated: false,
        error: action.payload,
        update:{
          loading : false,
          error : action.payload,
          succeeded : false
        }
      };

    case DELETING_HUB_SECTION:
      return {
        ...state,
        sectionDeletion:{
          loading : true,
          error : null,
          succeeded : false
        }
      };

    case DELETING_HUB_SECTION_SUCCEEDED: {
      const { layoutId, index } = action.payload as {
        layoutId: any;
        index: number;
      };
      
      const updatedRows = state.item.rows.filter(
        (_: any, i: number) => i !== index,
      );

      return {
        ...state,
        item: {
          ...state.item,
          rows: updatedRows,
        },
        sectionDeletion:{
          loading : false,
          error : null,
          succeeded : true
        }
      };
    }

    case DELETING_HUB_SECTION_FAILED:
      return {
        ...state,
        sectionDeletion:{
          loading : false,
          error : action.payload,
          succeeded : false
        }
      };

    case UPDATING_HUB_SECTION:
      return {
        ...state,
        item: {
          ...state.item,
          update:{
            loading : true,
            error : null,
            succeeded : false
          }
        },
      };

    case UPDATING_HUB_SECTION_SUCCEEDED: {
      const { response, create, rowIndex } = action.payload as UpdateHubSectionResponse

      const updatedRows = [...state.item.rows];
      if (create) {
        updatedRows.splice(rowIndex, 0, response);
      } else {
        updatedRows[rowIndex] = response;
      }

      return {
        ...state,
        item: {
          ...state.item,
          rows: updatedRows,
          update:{
            loading : false,
            error : null,
            succeeded : true,
            item : response
          },
        },
      };
    }

    case UPDATING_HUB_SECTION_FAILED:
      console.log(action.payload);
      return {
        ...state,
        item : {
          update : {
            ...state.item.update,
            loading : false,
            error : action.payload,
            succeeded : false
          }
        }
      };

    case DELETING_HUB_LAYOUT:
      return {
        ...state,
        deleting: true,
      };

    case DELETING_HUB_LAYOUT_SUCCEEDED: {
      const layoutId = action.payload;

      const updatedLayouts = state.layouts.filter(
        (layout: any) => layout.id !== layoutId,
      );

      return {
        ...state,
        layouts: updatedLayouts,
        deleting: false,
      };
    }

    case DELETING_HUB_LAYOUT_FAILED:
      return {
        ...state,
        deleting: false,
        error: action.payload,
      };

    // case ENABLING_ACCESS_FOR_USER_GROUPS:
    //   return {
    //     ...state,
    //     enablingAccess: true
    //   };

    // case ENABLING_ACCESS_FOR_USER_GROUPS_SUCCEEDED:
    //   const index = _.findIndex(state.layouts, layout => layout.id === action.payload.id);
    //   const layouts = state.layouts;

    //   layouts[index] = action.payload;

    //   return {
    //     ...state,
    //     layouts: layouts,
    //     enablingAccess: false
    //   };

    // case ENABLING_ACCESS_FOR_USER_GROUPS_FAILED:
    //   return {
    //     ...state,
    //     error: error,
    //     enablingAccess: false
    //   };

    case FETCHING_HUB_SECTION: {
      const id = action.payload;

      const section = state[id] || initialSectionState;

      return Object.assign({}, state, {
        ...state,
        sections: {
          ...state.sections,
          [id]: {
            ...section,
            loading: true,
            invalidated: true,
          },
        },
      });
    }

    case FETCHING_HUB_SECTION_SUCCEEDED: {
      const { id, response } = action.payload as {
        id: string;
        response: any;
      };

      let items = response.items;
      const smartFilter = response.smartFilter;

      const section = state[id] || initialSectionState;

      //comebac here for recommended categories
      // var items =
      //   items != null
      //     ? items
      //     : action.payload.response.recommendedCategories;

      if (id == "streams") {
        let sortByName = items.filter((item: any) => {
          return item.lastViewed == null;
        });

        let sortByView = items.filter((item: any) => {
          return item.lastViewed != null;
        });

        sortByName = orderBy(sortByName, [
          (sortByName: any) => sortByName.name,
        ]);
        sortByView = orderBy(
          sortByView,
          [(sortByView: any) => sortByView.lastViewed],
          ["desc"],
        );

        items = [...sortByView, ...sortByName];
      }

      return Object.assign({}, state, {
        ...state,
        sections: {
          ...state.sections,
          [id]: {
            ...section,
            loading: false,
            invalidated: false,
            items: items,
            smartFilter: smartFilter,
          },
        },
      });
    }

    case FETCHING_HUB_SECTION_FAILED: {
      const { id, error } = action.payload as {
        id: string;
        error: any;
      };

      const section = state.sections[id] || initialSectionState;

      return Object.assign({}, state, {
        ...state,
        sections: {
          ...state.sections,
          [id]: {
            ...section,
            loading: false,
            invalidated: false,
            items: null,
            error: error,
          },
        }
      });
    }

    case FETCHING_STREAMS_REQUEST: {
      return {
        ...state,
        streams: {
          ...state.streams,
          loading: true,
        },
      };
    }

    case FETCHING_STREAMS_SUCCEEDED: {
      const { response } = action.payload as FetchStreamsResponse;
      return {
        ...state,
        streams: {
          ...state.streams,
          loading: false,
          loaded: true,
          items: response,
          error: null,
        },
      };
    }

    case FETCHING_STREAMS_FAILED: {
      const { error } = action.payload as FetchStreamsErrorResponse;

      return {
        ...state,
        streams: {
          ...state.streams,
          loading: false,
          loaded: false,
          error: error,
        },
      };
    }

    case HIDE_SIDE_MENU: {
      return {
        ...state,
        hideSideMenu: action.payload
      };
    }

    case INVALIDATE_HUB_LAYOUT_UPDATE: {
      return {
        ...state,
        update: {
          loading : false,
          error : null,
          succeeded : false
        }
      }
    }

    case ENABLING_LAYOUT_ACCESS_FOR_USER_GROUPS: {
      return {
        ...state,
        enablingAccess: true
      }
    }

    case ENABLING_LAYOUT_ACCESS_FOR_USER_GROUPS_SUCCEEDED: {
      const response = action.payload;
      const index = _.findIndex(state.layouts, layout => layout.id === response.id);
      let layouts = [...state.layouts];
      
      if(index !== -1){
        layouts[index] = response;
      }

      return {
        ...state,
        layouts: layouts,
        enablingAccess: false
      }
    }

    case ENABLING_LAYOUT_ACCESS_FOR_USER_GROUPS_FAILED: {
      return {
        ...state,
        error: action.payload.error,
        enablingAccess: false
      }
    }

    case CREATE_HUB_LAYOUT: {
      return {
        ...state,
        defaultLayout: {
          creating: true,
          error: null,
          succeeded: false,
          item: null
        }
      }
    }

    case CREATING_HUB_LAYOUT_FAILED: {
      return {
        ...state,
        defaultLayout: {
          creating: false,
          error: action.payload,
          succeeded: false,
          item: null
        }
      }
    }

    case INVALIDATE_HUB_LAYOUT_CREATION: {
      return {
        ...state,
        defaultLayout: {
          creating: false,
          error: null,
          succeeded: false,
          item: null
        }
      }
    }


    case CREATING_HUB_LAYOUT_SUCCEEDED: {
      const response = action.payload as HubLayout;

      return {
        ...state,
        loading: false,
        invalidated: false,
        item: response,
        defaultLayout: {
          creating: false,
          error: null,
          succeeded: true,
          item: action.payload
        }
      };
    }

    default:
      return state;
  }
}
